import { IDocumentDetails } from '../../../@types/models';
import {
    type MultipleMailArchiveInfoMap,
    getMsalGraphScopes,
    getMultipleArchivedMailInfo,
    getSelectedMessageIdsResult,
    getTargetInbox,
} from '../../../services/api/dataServiceOutlook';
import { getMsalAccessToken } from '../../../services/api/msalUtil';

export const resolveSelectedMessageIdsForUi = async () => {
    try {
        const targetInbox = await getTargetInbox();
        const token = await getMsalAccessToken(await getMsalGraphScopes());
        return await getSelectedMessageIdsResult(token ?? undefined, targetInbox);
    } catch {
        return await getSelectedMessageIdsResult();
    }
};

export const normalizeMessageId = (messageId: string): string => {
    const trimmedMessageId = messageId.trim();

    return trimmedMessageId.toLowerCase();
};

const getStringValue = (source: Record<string, unknown>, keys: string[]): string => {
    for (const key of keys) {
        const value = source[key];
        if (typeof value === 'string' && value.trim().length > 0) {
            return value;
        }
    }

    return '';
};

const getOptionalStringValue = (source: Record<string, unknown>, keys: string[]): string | undefined => {
    const value = getStringValue(source, keys);
    return value || undefined;
};

const getOptionalNumberValue = (source: Record<string, unknown>, key: string): number | undefined => {
    const value = source[key];
    return typeof value === 'number' ? value : undefined;
};

const getOptionalBooleanValue = (source: Record<string, unknown>, key: string): boolean | undefined => {
    const value = source[key];
    return typeof value === 'boolean' ? value : undefined;
};

const normalizeArchivedDocument = (archived: unknown): IDocumentDetails | null => {
    if (!archived || typeof archived !== 'object' || Array.isArray(archived)) {
        return null;
    }

    const document = archived as Record<string, unknown>;
    const documentNumber = getStringValue(document, ['DocumentNumber', 'Documentnumber']);

    return {
        AccessCodeDesc: getStringValue(document, ['AccessCodeDesc']),
        CaseDescription: getStringValue(document, ['CaseDescription']),
        ArchiveCode: getStringValue(document, ['ArchiveCode', 'CategoryCode']),
        CaseNumber: getStringValue(document, ['CaseNumber']),
        CategoryCode: getStringValue(document, ['CategoryCode']),
        DocumentCategory: getOptionalStringValue(document, ['DocumentCategory']),
        DocumentDetailsLink: getStringValue(document, ['DocumentDetailsLink']),
        DocumentTitle: getStringValue(document, ['DocumentTitle']),
        DocumentNumber: documentNumber,
        DocumentRecno: getOptionalNumberValue(document, 'DocumentRecno'),
        OrgUnitName: getStringValue(document, ['OrgUnitName']),
        Paragraph: getStringValue(document, ['Paragraph']),
        ResponsibleName: getStringValue(document, ['ResponsibleName']),
        Documentnumber: getOptionalStringValue(document, ['Documentnumber', 'DocumentNumber']),
        FileStatus: getOptionalNumberValue(document, 'FileStatus'),
        IsInImportArchive: getOptionalBooleanValue(document, 'IsInImportArchive'),
        InsertMessage: getOptionalStringValue(document, ['InsertMessage']),
        ImportArchiveLink: getOptionalStringValue(document, ['ImportArchiveLink']),
    };
};

const normalizeArchivedList = (archived: unknown): IDocumentDetails[] => {
    if (Array.isArray(archived)) {
        return archived
            .map((item) => normalizeArchivedDocument(item))
            .filter((item): item is IDocumentDetails => item !== null);
    }

    if (!archived || typeof archived !== 'object') {
        return [];
    }

    // Backward compatibility: server may still return a single object during rollout.
    const normalizedDocument = normalizeArchivedDocument(archived);
    return normalizedDocument ? [normalizedDocument] : [];
};

export const getArchivedItemIds = (resData: MultipleMailArchiveInfoMap): string[] => {
    const archivedItemIds: string[] = [];

    for (const [itemId, archivedList] of Object.entries(resData)) {
        if (normalizeArchivedList(archivedList).length > 0) {
            archivedItemIds.push(itemId);
        }
    }

    return archivedItemIds;
};

export const getArchivedDocuments = (resData: MultipleMailArchiveInfoMap): IDocumentDetails[] => {
    const archivedDocuments: IDocumentDetails[] = [];

    for (const archivedList of Object.values(resData)) {
        archivedDocuments.push(...normalizeArchivedList(archivedList));
    }

    return archivedDocuments;
};

export const getArchivedDocumentsForItemIds = (
    resData: MultipleMailArchiveInfoMap,
    itemIds: string[]
): IDocumentDetails[] => {
    const requestedItemIds = new Set(itemIds.map(normalizeMessageId));
    const archivedDocuments: IDocumentDetails[] = [];

    for (const [itemId, archivedList] of Object.entries(resData)) {
        if (requestedItemIds.has(normalizeMessageId(itemId))) {
            archivedDocuments.push(...normalizeArchivedList(archivedList));
        }
    }

    return archivedDocuments;
};

export const areAllSelectedMailsArchived = (selectedItemIds: string[], archivedItemIds: string[]): boolean => {
    if (selectedItemIds.length === 0) {
        return false;
    }

    const archivedItemIdSet = new Set(archivedItemIds.map(normalizeMessageId));

    for (const itemId of selectedItemIds) {
        if (!archivedItemIdSet.has(normalizeMessageId(itemId))) {
            return false;
        }
    }

    return true;
};

export const getMultipleArchivedMailInfoAsync = async (): Promise<MultipleMailArchiveInfoMap> => {
    return await new Promise((resolve, reject) => {
        void getMultipleArchivedMailInfo(resolve, reject);
    });
};

export const hasSelectedItemsChangedSupport = (): boolean => {
    return Boolean(
        Office.context.requirements?.isSetSupported?.('Mailbox', '1.13')
        && Office.EventType.SelectedItemsChanged
    );
};

export const removeSelectedItemsChangedHandler = (handler: () => void): void => {
    (Office.context.mailbox.removeHandlerAsync as unknown as (
        eventType: Office.EventType,
        options: { handler: () => void },
        callback: () => void
    ) => void)(Office.EventType.SelectedItemsChanged, { handler }, () => undefined);
};

/**
 * Returns the deduplicated IDs still to be archived, or null when all selected mails are already archived.
 * When archivedItemIds is empty, all selected IDs are returned (first archive run).
 */
export const computeRemainingItemIds = (
    selectedItemIds: string[],
    archivedItemIds: string[]
): string[] | null => {
    if (archivedItemIds.length === 0) {
        return Array.from(new Set(selectedItemIds));
    }

    const archivedItemIdSet = new Set(archivedItemIds.map(normalizeMessageId));
    const remaining = selectedItemIds.filter((id) => !archivedItemIdSet.has(normalizeMessageId(id)));

    return remaining.length === 0 ? null : Array.from(new Set(remaining));
};